import React, { useState, useEffect, useCallback } from "react";
import {
    Offcanvas, OffcanvasHeader, OffcanvasBody,
    Badge, Spinner, Row, Col, Input, Label, Button, FormGroup,
} from "reactstrap";
import { FiRefreshCw } from "react-icons/fi";
import { EmptyState } from "../../Components/Common/DashboardKit";
import SupportReplyForm from "./SupportReplyForm";
import { statusTone, priorityTone } from "./supportConstants";
import {
    getTicket, replyToTicket, updateTicket, parseSupportError,
} from "./supportService";

const fmt = (d) =>
    d ? new Date(d).toLocaleString("en-US", {
        month: "short", day: "numeric", year: "numeric",
        hour: "2-digit", minute: "2-digit",
    }) : "—";

/**
 * Right-side drawer showing a single ticket: header fields, original
 * description, the conversation thread, a reply composer, and (for callers
 * with all-ticket access) inline status / priority / group / agent updates.
 *
 * Every mutation is authorized server-side; this component only renders the
 * controls. Regular users still see status, but the backend rejects updates
 * they aren't allowed to make and the parent surfaces the clean error.
 */
const SupportTicketDetailDrawer = ({
    accountKey,
    ticketId,
    isOpen,
    toggle,
    onChanged,        // (ticket) => void — notify list to refresh a row
    notify,           // (type, message) => void — toast helper from parent
    statuses = [],
    priorities = [],
    groups = [],
    agents = [],
    canManage = false, // all-ticket access → show update controls
}) => {
    const [loading, setLoading] = useState(false);
    const [ticket, setTicket] = useState(null);
    const [replying, setReplying] = useState(false);
    const [savingField, setSavingField] = useState(null);

    const load = useCallback(async () => {
        if (!accountKey || !ticketId) return;
        setLoading(true);
        try {
            const data = await getTicket(accountKey, ticketId);
            setTicket(data);
        } catch (err) {
            const { message } = parseSupportError(err);
            notify?.("error", message);
            setTicket(null);
        } finally {
            setLoading(false);
        }
    }, [accountKey, ticketId, notify]);

    useEffect(() => {
        if (isOpen && ticketId) load();
        if (!isOpen) setTicket(null);
    }, [isOpen, ticketId, load]);

    const handleReply = async (body) => {
        setReplying(true);
        try {
            await replyToTicket(accountKey, ticketId, body);
            notify?.("success", "Reply sent.");
            await load();
            return true;
        } catch (err) {
            const { message } = parseSupportError(err);
            notify?.("error", message);
            return false;
        } finally {
            setReplying(false);
        }
    };

    const patch = async (field, value) => {
        setSavingField(field);
        try {
            const payload = {};
            if (field === "status") payload.status = Number(value);
            if (field === "priority") payload.priority = Number(value);
            if (field === "groupId") payload.groupId = value ? Number(value) : null;
            if (field === "responderId") payload.responderId = value ? Number(value) : null;
            const updated = await updateTicket(accountKey, ticketId, payload);
            setTicket(updated);
            onChanged?.(updated);
            notify?.("success", "Ticket updated.");
        } catch (err) {
            const { message } = parseSupportError(err);
            notify?.("error", message);
            await load(); // re-sync controls to server truth
        } finally {
            setSavingField(null);
        }
    };

    return (
        <Offcanvas isOpen={isOpen} toggle={toggle} direction="end" style={{ width: "min(640px, 95vw)" }}>
            <OffcanvasHeader toggle={toggle}>
                {ticket ? `#${ticket.id} · ${ticket.subject}` : "Ticket"}
            </OffcanvasHeader>
            <OffcanvasBody>
                {loading && (
                    <div className="text-center py-5">
                        <Spinner /> <div className="text-muted mt-2">Loading ticket…</div>
                    </div>
                )}

                {!loading && !ticket && (
                    <EmptyState
                        icon="ri-error-warning-line"
                        title="Could not load ticket"
                        message="The ticket may not exist or you may not have access to it."
                        tone="warning"
                    />
                )}

                {!loading && ticket && (
                    <>
                        {/* Header chips */}
                        <div className="d-flex flex-wrap gap-2 mb-3">
                            <Badge color={statusTone(ticket.statusLabel)} pill>{ticket.statusLabel}</Badge>
                            <Badge color={priorityTone(ticket.priorityLabel)} pill>{ticket.priorityLabel}</Badge>
                            <Badge color="light" className="text-dark">{ticket.accountLabel}</Badge>
                        </div>

                        <Row className="g-2 mb-3">
                            <Col xs={6}><div className="text-muted small">Requester</div><div>{ticket.requesterName || ticket.requesterEmail || "—"}</div></Col>
                            <Col xs={6}><div className="text-muted small">Requester email</div><div>{ticket.requesterEmail || "—"}</div></Col>
                            <Col xs={6}><div className="text-muted small">Group</div><div>{ticket.groupName || "—"}</div></Col>
                            <Col xs={6}><div className="text-muted small">Assigned agent</div><div>{ticket.agentName || "—"}</div></Col>
                            <Col xs={6}><div className="text-muted small">Created</div><div>{fmt(ticket.createdAt)}</div></Col>
                            <Col xs={6}><div className="text-muted small">Updated</div><div>{fmt(ticket.updatedAt)}</div></Col>
                        </Row>

                        {/* Update controls — only for all-ticket access */}
                        {canManage && (
                            <div className="border rounded p-3 mb-3 bg-light-subtle">
                                <div className="fw-medium mb-2">Update ticket</div>
                                <Row className="g-2">
                                    <Col md={6}>
                                        <Label className="form-label small">Status</Label>
                                        <Input type="select" value={ticket.status}
                                            disabled={savingField === "status"}
                                            onChange={(e) => patch("status", e.target.value)}>
                                            {statuses.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                                        </Input>
                                    </Col>
                                    <Col md={6}>
                                        <Label className="form-label small">Priority</Label>
                                        <Input type="select" value={ticket.priority}
                                            disabled={savingField === "priority"}
                                            onChange={(e) => patch("priority", e.target.value)}>
                                            {priorities.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                                        </Input>
                                    </Col>
                                    <Col md={6}>
                                        <Label className="form-label small">Group</Label>
                                        <Input type="select" value={ticket.groupId || ""}
                                            disabled={savingField === "groupId"}
                                            onChange={(e) => patch("groupId", e.target.value)}>
                                            <option value="">— Unassigned —</option>
                                            {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                                        </Input>
                                    </Col>
                                    <Col md={6}>
                                        <Label className="form-label small">Assigned agent</Label>
                                        <Input type="select" value={ticket.responderId || ""}
                                            disabled={savingField === "responderId"}
                                            onChange={(e) => patch("responderId", e.target.value)}>
                                            <option value="">— Unassigned —</option>
                                            {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                                        </Input>
                                    </Col>
                                </Row>
                            </div>
                        )}

                        {/* Description */}
                        <div className="mb-3">
                            <div className="text-muted small mb-1">Description</div>
                            <div className="border rounded p-3">
                                {ticket.descriptionText
                                    ? <div style={{ whiteSpace: "pre-wrap" }}>{ticket.descriptionText}</div>
                                    : <span className="text-muted">—</span>}
                            </div>
                        </div>

                        {/* Conversation thread */}
                        <div className="d-flex align-items-center justify-content-between mb-2">
                            <div className="fw-medium">Conversation</div>
                            <Button size="sm" color="light" onClick={load}>
                                <FiRefreshCw className="me-1" /> Refresh
                            </Button>
                        </div>

                        {(!ticket.conversations || ticket.conversations.length === 0) ? (
                            <div className="text-muted small mb-3">No replies yet.</div>
                        ) : (
                            <div className="mb-3">
                                {ticket.conversations.map((c) => (
                                    <div key={c.id}
                                        className={`border rounded p-3 mb-2 ${c.incoming ? "" : "bg-light-subtle"}`}>
                                        <div className="d-flex justify-content-between">
                                            <span className="fw-medium">
                                                {c.fromEmail || (c.incoming ? "Requester" : "Agent")}
                                                {c.private && <Badge color="warning" className="ms-2">Private note</Badge>}
                                            </span>
                                            <span className="text-muted small">{fmt(c.createdAt)}</span>
                                        </div>
                                        <div className="mt-2" style={{ whiteSpace: "pre-wrap" }}>
                                            {c.bodyText || ""}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Reply */}
                        <div className="border-top pt-3">
                            <SupportReplyForm onSubmit={handleReply} submitting={replying} />
                        </div>
                    </>
                )}
            </OffcanvasBody>
        </Offcanvas>
    );
};

export default SupportTicketDetailDrawer;
